/**
 * Prototype-only: what the next import does, and how the screens around it are
 * arranged.
 *
 * The scenario is read when the file is submitted and frozen onto that batch,
 * so switching it mid-run never rewrites a run already in flight. The dials
 * below it (where re-import lives, what happens after Save import, whether the
 * catalogue has been fixed) are read when they are used, so they can be flipped
 * between runs — and between a failed import and its re-import.
 */
import { useSyncExternalStore } from "react";

export type ImportScenario =
  | "release-without-tracks"
  | "clean"
  | "upload-rejected"
  | "upload-500"
  | "analysis-failed"
  | "row-errors"
  | "canceled"
  | "preview-failed"
  | "confirm-failed";

/** The flow this prototype was built for: catalog lands, revenue does not. */
export const FOCUS_SCENARIO: { id: ImportScenario; label: string; hint: string } = {
  id: "release-without-tracks",
  label: "Release without tracks",
  hint: "Catalog imports fine; revenue can't be allocated.",
};

/** The error states the prototype already reproduced, left exactly as they were. */
export const SCENARIOS: { id: ImportScenario; label: string; hint: string }[] = [
  { id: "clean", label: "No errors", hint: "Upload → review → import. The baseline." },
  {
    id: "upload-rejected",
    label: "File rejected (400)",
    hint: "Backend refuses the file. Error shows under the dropzone.",
  },
  {
    id: "upload-500",
    label: "Upload fails (500)",
    hint: "Same red line under the dropzone, generic message.",
  },
  {
    id: "analysis-failed",
    label: "Analysis fails",
    hint: 'Batch goes "failed" → full-screen Import failed.',
  },
  {
    id: "row-errors",
    label: "Rows with errors",
    hint: "Error + conflict rows in review; Save is blocked.",
  },
  {
    id: "canceled",
    label: "Batch canceled",
    hint: "Silent drop back to the upload form — no message at all.",
  },
  {
    id: "preview-failed",
    label: "Preview call fails",
    hint: "Review step with empty tables, no error shown.",
  },
  {
    id: "confirm-failed",
    label: "Import confirm fails",
    hint: "Toast on Save & Import; the review step stays put.",
  },
];

const ALL_SCENARIOS = [FOCUS_SCENARIO, ...SCENARIOS];

/** What happens once the catalog part of a failing import has landed. */
export type AfterSaveVariant = "v1" | "v2";
export const AFTER_SAVE_LABELS: Record<AfterSaveVariant, string> = {
  v1: "V1 – Complete screen",
  v2: "V2 – Straight to list",
};

/** Where a failed statement offers its re-import. */
export type ReimportPlacement = "menu" | "row";
export const REIMPORT_LABELS: Record<ReimportPlacement, string> = {
  menu: "Menu",
  row: "Button in row",
};

const KEYS = {
  scenario: "rie-scenario",
  afterSave: "rie-after-save",
  reimport: "rie-reimport-placement",
  catalogFixed: "rie-catalog-fixed",
} as const;

const EVENT = "rie-scenario-change";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the change still applies to this page view */
  }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}

export function getScenario(): ImportScenario {
  const raw = read(KEYS.scenario) as ImportScenario | null;
  if (raw && ALL_SCENARIOS.some((option) => option.id === raw)) return raw;
  return FOCUS_SCENARIO.id;
}

export function setScenario(scenario: ImportScenario): void {
  write(KEYS.scenario, scenario);
}

export function getAfterSave(): AfterSaveVariant {
  return read(KEYS.afterSave) === "v2" ? "v2" : "v1";
}

export function getReimportPlacement(): ReimportPlacement {
  return read(KEYS.reimport) === "row" ? "row" : "menu";
}

/** Whether the missing tracks have been added, so a re-import can succeed. */
export function isCatalogFixed(): boolean {
  return read(KEYS.catalogFixed) === "true";
}

export function useImportScenario(): {
  scenario: ImportScenario;
  setScenario: (next: ImportScenario) => void;
} {
  const scenario = useSyncExternalStore(subscribe, getScenario, () => FOCUS_SCENARIO.id);
  return { scenario, setScenario };
}

export function useAfterSave(): {
  afterSave: AfterSaveVariant;
  setAfterSave: (next: AfterSaveVariant) => void;
} {
  const afterSave = useSyncExternalStore(subscribe, getAfterSave, () => "v1" as AfterSaveVariant);
  return { afterSave, setAfterSave: (next) => write(KEYS.afterSave, next) };
}

export function useReimportPlacement(): {
  reimportPlacement: ReimportPlacement;
  setReimportPlacement: (next: ReimportPlacement) => void;
} {
  const reimportPlacement = useSyncExternalStore(
    subscribe,
    getReimportPlacement,
    () => "menu" as ReimportPlacement,
  );
  return { reimportPlacement, setReimportPlacement: (next) => write(KEYS.reimport, next) };
}

export function useCatalogFixed(): {
  catalogFixed: boolean;
  setCatalogFixed: (next: boolean) => void;
} {
  const catalogFixed = useSyncExternalStore(subscribe, isCatalogFixed, () => false);
  return { catalogFixed, setCatalogFixed: (next) => write(KEYS.catalogFixed, String(next)) };
}
